import clsx from 'clsx';
import React, { type ReactElement, useState } from 'react';

import { TextButton } from '@components/Button/variants';
import { useEventTracking } from '@hooks';
import { project_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';

import ProSecondaryButton from './ProSecondaryButton';
import { ProFocusOptionProps } from './interfaces';

const LESS = 'less';
const MORE = 'more';
const VIEW_THE_LIST = 'View the list';

const ProFocusOption = ({
  buttonClassName,
  containerClassName,
  description,
  descriptionClassName,
  index,
  isProSubscriberNotBot,
  secondaryTitle = 'Database',
  slug,
  title,
  titleClassName,
}: ProFocusOptionProps): ReactElement => {
  const { trackEvent } = useEventTracking({ location: TrackingLocation.FocusOnAI });
  const [isReadingMore, setIsReadingMore] = useState<boolean>(false);
  const readingStatusText = `Read ${isReadingMore ? LESS : MORE}`;

  const handleReadButtonClick = () => {
    setIsReadingMore((currReadingState) => !currReadingState);
  };

  const handleClickTracking = () => {
    trackEvent(
      click({
        clickTarget: ClickTarget.ReadMore,
        clickText: readingStatusText,
        contentIndex: index,
      })
    );
  };

  return (
    <article
      className={clsx(
        'relative mt-24 w-full border-0 border-t border-solid border-primary-pink pt-24 first-of-type:border-none first-of-type:pt-0 sm:ml-24 sm:mt-0 sm:border-l sm:border-t-0 sm:pl-24 sm:pt-0 sm:first-of-type:!ml-0 sm:first-of-type:!pl-0 md:ml-[46px] md:pl-[46px] lg:ml-32 lg:pl-32',
        containerClassName
      )}
    >
      <h4
        className={clsx(
          'm-0 font-suisse-screen text-caption-lg font-normal md:text-headline-alt-sm',
          titleClassName
        )}
      >
        {title}
        {secondaryTitle && (
          <>
            {' '}
            <span className="block text-primary-magenta">{secondaryTitle}</span>
          </>
        )}
      </h4>
      <p
        className={clsx(
          'm-0 mt-8 font-suisse-intl text-form md:text-body-lg',
          descriptionClassName,
          {
            'line-clamp-2 sm:line-clamp-3 md:line-clamp-4 lg:line-clamp-3': !isReadingMore,
          }
        )}
      >
        {description}
      </p>
      <TextButton
        as="button"
        className={clsx(
          'm-0 !w-auto border-0 p-0 text-left font-suisse-intl text-form font-medium md:text-body-lg',
          buttonClassName
        )}
        onClick={handleReadButtonClick}
        onClickCapture={handleClickTracking}
      >
        {readingStatusText}
      </TextButton>
      {isProSubscriberNotBot && (
        <ProSecondaryButton
          buttonText={VIEW_THE_LIST}
          clickText={VIEW_THE_LIST}
          url={project_path(slug)}
        />
      )}
    </article>
  );
};

export default ProFocusOption;
